import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Film, ArrowLeft, ChevronRight, ChevronDown, Search,
  Upload, FileText, Layers, Play, Mic, Download,
  Users, UserPlus, FolderOpen, Wand2, Video, Music,
  Image, Settings, HelpCircle, BookOpen, Sparkles,
  CheckCircle, ListChecks, Clapperboard, Headphones,
  Camera, MessageSquare, Star, Trash2, RefreshCw
} from "lucide-react";

// ============== DIRECTOR GUIDE ==============
const directorSections = [
  {
    id: "director-welcome",
    title: "Welcome, Director!",
    icon: Sparkles,
    content: [
      {
        title: "Your Animation Journey Starts Here",
        description: "Hey there! Welcome to anim8pro. Think of us as your animation production team - we handle the technical heavy lifting so you can focus on what matters: telling your story. You bring the creative vision, characters, and script. We'll help you bring it all to life."
      },
      {
        title: "The Big Picture",
        description: "Here's what you'll be doing (don't worry, we'll cover each step in detail):",
        list: [
          { name: "1. Gather Your Assets", desc: "Upload your backgrounds, characters, and props - your artwork, your style" },
          { name: "2. Add Your Script", desc: "Paste in your screenplay or write it directly" },
          { name: "3. Create Your Shot List", desc: "We'll help break your script into individual shots" },
          { name: "4. Animate Each Shot", desc: "Use our simple 5-step workflow to bring scenes to life" },
          { name: "5. Edit & Export", desc: "Put it all together in the Project Editor and export your finished video" }
        ]
      },
      {
        title: "Your Art, Your Vision",
        description: "A quick note: anim8pro works with YOUR artwork. We're not generating characters or replacing your creative decisions - we're just helping you animate what you've already designed. Think of it like having a tireless animation assistant who never sleeps."
      }
    ]
  },
  {
    id: "director-assets",
    title: "Step 1: Import Your Assets",
    icon: Upload,
    content: [
      {
        title: "What You'll Need",
        description: "Before you start animating, you'll want to gather your visual assets. Here's what goes where:",
        list: [
          { name: "Sets", icon: Image, desc: "These are your backgrounds - coffee shops, parks, offices, etc. Use JPG, PNG, or WebP files." },
          { name: "Characters", icon: Users, desc: "Your cast! PNG files with transparent backgrounds work best so characters can be placed on any set." },
          { name: "Items", icon: Layers, desc: "Props and objects that appear in scenes - phones, cups, books, etc." }
        ]
      },
      {
        title: "How to Upload",
        steps: [
          "Open your project and look for the Asset Library on the left side",
          "Click on the tab for what you're uploading (Sets, Characters, or Items)",
          "Hit the Upload button or just drag and drop your files",
          "Pro tip: Use 'Smart Import' to upload entire folders at once - it'll organize everything for you!"
        ]
      },
      {
        title: "Optional: Voice & Music",
        description: "You can also upload these now or add them later when you need them:",
        list: [
          { name: "Voice Files", icon: Mic, desc: "Pre-recorded dialogue (MP3 or WAV). You can also have actors record directly in the app." },
          { name: "Music", icon: Music, desc: "Background music and sound effects for your final edit." },
          { name: "Motion Refs", icon: Video, desc: "Reference videos showing how you want characters to move. Great for complex actions." }
        ]
      }
    ]
  },
  {
    id: "director-script",
    title: "Step 2: Add Your Script",
    icon: FileText,
    content: [
      {
        title: "Getting Your Script In",
        description: "Click on the 'Script' tab in your project view. You can type directly or paste from your favorite writing app. The system works best with standard screenplay format, but don't stress about it being perfect."
      },
      {
        title: "Script Format That Works Best",
        description: "Here's what a good script looks like:",
        code: `INT. COFFEE SHOP - DAY

SARAH walks in and spots DEAN at a corner table.

SARAH: Hey! Sorry I'm late.

DEAN: No worries. I ordered you a latte.

(Sarah sits down, looking relieved)

SARAH: You're a lifesaver.`
      },
      {
        title: "Quick Formatting Tips",
        list: [
          { name: "Scene Headers", desc: "Start with INT. (inside) or EXT. (outside), then the location and time of day" },
          { name: "Character Names", desc: "Write them in ALL CAPS before their dialogue" },
          { name: "Actions", desc: "Put character actions in (parentheses) or write them as regular lines" },
          { name: "Don't overthink it", desc: "The system is pretty smart about figuring out what's what" }
        ]
      }
    ]
  },
  {
    id: "director-script-tools",
    title: "Step 3: Use Script Tools",
    icon: Wand2,
    content: [
      {
        title: "Your Script Tools Toolkit",
        description: "Click 'Script Tools' from your project to access these powerful helpers. They'll save you tons of time!"
      },
      {
        title: "Asset Inventory (Check What's Missing)",
        description: "This scans your script and tells you exactly what assets you need.",
        steps: [
          "Click 'Generate Inventory'",
          "The system reads your script and lists all characters, locations, and props",
          "Green checkmark = you have it. Red 'Missing' = you need to upload it",
          "Click 'Upload' next to any missing item to add it right there"
        ]
      },
      {
        title: "Create Shot List (The Magic Button)",
        description: "This is where the real magic happens. One click and your script becomes a shot list.",
        steps: [
          "Click 'Create Shot List'",
          "Watch as your script gets broken into individual shots",
          "Each shot gets a descriptive name like 'Shot 1: SARAH enters'",
          "Shots are automatically grouped into scenes based on location",
          "Head to your Shot List to start animating!"
        ]
      },
      {
        title: "Generate Script PDFs",
        description: "Need to send scripts to your voice actors? We've got you.",
        steps: [
          "Click 'Generate Script PDF'",
          "Pick a character from the dropdown",
          "Choose 'Full Script' (everything with their lines highlighted) or 'Sides' (just their lines)",
          "Download and send it off!"
        ]
      }
    ]
  },
  {
    id: "director-actors",
    title: "Working with Voice Actors",
    icon: UserPlus,
    content: [
      {
        title: "When to Invite Actors",
        description: "You can invite actors at any point - before or after creating your shot list. They'll be able to see their assigned scenes and record their lines directly in the app."
      },
      {
        title: "How to Invite Someone",
        steps: [
          "Go to 'Cast & Crew' in your project header",
          "Click 'Invite Actor'",
          "Enter their email address",
          "They'll get a welcome email with login instructions",
          "Once they're in, assign them to characters"
        ]
      },
      {
        title: "Assigning Characters",
        description: "After inviting actors, you need to tell the system who plays who.",
        steps: [
          "Find the actor in your Cast & Crew list",
          "Click 'Assign' next to their name",
          "Select which character(s) they're voicing",
          "They'll now see those scenes in their Actor Portal"
        ]
      },
      {
        title: "What Actors See",
        description: "When actors log in, they get a streamlined view focused on recording. They can:",
        list: [
          { name: "View their scenes", desc: "Only scenes with their character's dialogue" },
          { name: "Read the script context", desc: "So they understand what's happening" },
          { name: "Record audio takes", desc: "Using their microphone or uploading files" },
          { name: "Record video takes", desc: "Motion reference to help guide animation" }
        ]
      }
    ]
  },
  {
    id: "director-animate",
    title: "Step 4: Animate Your Shots",
    icon: Play,
    content: [
      {
        title: "The Animation Studio",
        description: "Click on any shot in your Shot List to open the Animation Studio. This is where you'll bring each scene to life. It's a simple 5-step process, and you're in control at every stage."
      },
      {
        title: "Stage 1: COMPOSE - Set Up Your Shot",
        description: "This is where you arrange everything visually - you're the director here.",
        steps: [
          "Pick a background from your Sets",
          "Drag characters onto the canvas",
          "Resize and position them using the controls on the right",
          "Flip characters horizontally if they need to face a different direction",
          "Adjust the background zoom and position if needed"
        ]
      },
      {
        title: "Stage 2: PREVIEW - Check Before You Animate",
        description: "Review your setup and tell us exactly how you want the scene to move.",
        steps: [
          "Look at the composite image - this is what we'll animate for you",
          "Review the suggested animation description",
          "Edit the description if you want different movement or action - be specific!",
          "Optional: Add reference images to guide the animation style",
          "When you're happy, hit 'Create Animation'"
        ]
      },
      {
        title: "Stage 3: ANIMATE - Watch It Come to Life",
        description: "Now we generate your animation based on your direction. This takes about 1-3 minutes.",
        steps: [
          "Grab a coffee while it renders",
          "When it's done, preview the result",
          "Not quite right? Click 'Create New Take' to try again - each result is unique",
          "Each take is saved - you can compare and pick your favorite",
          "When you've got a winner, move to the next stage"
        ]
      },
      {
        title: "Stage 4: LIP SYNC - Add the Voice",
        description: "If your shot has dialogue, this is where we sync the mouth movement to your voice recording.",
        steps: [
          "Select a voice file from your assets (or record one right here!)",
          "Click 'Apply Lip Sync'",
          "We'll animate the character's mouth to match the audio",
          "Preview the result - the timing should match naturally",
          "Create multiple takes with different voice files if needed"
        ]
      },
      {
        title: "Stage 5: PRINT - Finish the Shot",
        description: "Your shot is done! Time to save it.",
        steps: [
          "Review your final video one more time",
          "Click 'Print Shot' to lock in your work",
          "The shot is now ready for the Project Editor",
          "Move on to the next shot and repeat!"
        ]
      },
      {
        title: "Shortcut: No Dialogue?",
        description: "If a shot has no speaking parts (like an establishing shot or action sequence), you can skip straight from Animate to Print. Just click 'Print' once you're happy with the animation."
      }
    ]
  },
  {
    id: "director-editor",
    title: "Step 5: Edit & Export",
    icon: Film,
    content: [
      {
        title: "The Project Editor",
        description: "Once all your shots are printed, head to the Project Editor to assemble your final video. Click 'Project Editor' in the header."
      },
      {
        title: "Arranging Your Shots",
        steps: [
          "All your printed shots appear in the timeline",
          "Drag them to rearrange the order",
          "Trim clips by dragging their edges",
          "Add transitions between scenes if desired"
        ]
      },
      {
        title: "Adding Music & Sound",
        steps: [
          "Click the audio track to add music",
          "Select from your uploaded music files",
          "Adjust volume levels for each clip",
          "Add multiple audio tracks for layering"
        ]
      },
      {
        title: "Exporting Your Video",
        steps: [
          "Click 'Export' when you're happy with the edit",
          "Choose your quality settings",
          "Wait for the render to complete",
          "Download your finished cartoon!"
        ]
      }
    ]
  },
  {
    id: "director-tips",
    title: "Pro Tips & Troubleshooting",
    icon: Star,
    content: [
      {
        title: "Tips from the Pros",
        list: [
          { name: "Use PNG for characters", desc: "Transparent backgrounds = cleaner compositions" },
          { name: "Keep character images consistent", desc: "Use the same style/pose references throughout for visual continuity" },
          { name: "Generate multiple takes", desc: "Results vary - 2-3 takes usually gives you a great option to choose from" },
          { name: "Be specific in your descriptions", desc: "The more detail you give about how you want movement to look, the better the results" },
          { name: "Save often", desc: "Click Save after major changes. Your work is precious!" },
          { name: "Start simple", desc: "Do a short scene first to learn the workflow before tackling your epic" }
        ]
      },
      {
        title: "Common Questions",
        list: [
          { name: "Animation looks weird?", desc: "Try editing the description in Preview stage. Be specific about the action you want - you're the director!" },
          { name: "Lip sync off?", desc: "The audio might be slightly long or short. Try a different take or adjust the timing." },
          { name: "Can't find an asset?", desc: "Check the Asset Inventory in Script Tools - it shows what's missing." },
          { name: "Actor didn't get the email?", desc: "Go to Cast & Crew and click the refresh icon to resend their invite." },
          { name: "Shot list already exists?", desc: "The 'Create Shot List' button is disabled once you have shots. Delete them in Shot List view if you want to regenerate." }
        ]
      },
      {
        title: "Remember: You're in Charge",
        description: "anim8pro is a tool that works for you, not the other way around. Every creative decision - from character design to scene composition to the final edit - is yours. We just help you execute your vision faster than traditional animation pipelines."
      }
    ]
  }
];

// ============== ACTOR GUIDE ==============
const actorSections = [
  {
    id: "actor-welcome",
    title: "Welcome, Actor!",
    icon: Sparkles,
    content: [
      {
        title: "You're Here to Bring Characters to Life",
        description: "Hey there! Thanks for joining the project. Your performance is what gives these characters their soul - the voice, the emotion, the personality. The director handles the visuals, but you're the one who makes the audience feel something."
      },
      {
        title: "What You'll Be Doing",
        description: "Here's the quick version of your workflow:",
        list: [
          { name: "1. Check your projects", desc: "See which projects you've been assigned to" },
          { name: "2. Read the script", desc: "Understand the context and your character's role" },
          { name: "3. Record your takes", desc: "Do audio or video recordings of your lines" },
          { name: "4. Mark your favorites", desc: "Help the director know which takes you liked best" }
        ]
      }
    ]
  },
  {
    id: "actor-getting-started",
    title: "Getting Started",
    icon: BookOpen,
    content: [
      {
        title: "Logging In",
        description: "You should have received an email from the director with a link to set up your account. Click that link, create your password, and you're in!"
      },
      {
        title: "Your Actor Portal",
        description: "When you log in, you'll see the Actor Portal - a streamlined view designed just for you. No complicated menus, just the stuff you need."
      },
      {
        title: "Finding Your Projects",
        description: "On the main screen, you'll see cards for each project you've been invited to. Click on one to open it and see your assigned shots."
      }
    ]
  },
  {
    id: "actor-reading",
    title: "Reading the Script",
    icon: FileText,
    content: [
      {
        title: "Understanding Your Scenes",
        description: "Before recording, take a moment to understand what's happening in each scene. Click on a shot to see the full context."
      },
      {
        title: "What You'll See",
        list: [
          { name: "The script excerpt", desc: "The dialogue and action for this specific shot" },
          { name: "Scene context", desc: "What's happening before and after (so you get the emotional flow)" },
          { name: "Your character's lines", desc: "Highlighted so they're easy to spot" },
          { name: "Director notes", desc: "Any specific direction for this scene" }
        ]
      },
      {
        title: "Tip: Read Everything First",
        description: "Before you start recording, read through all your scenes. Get a feel for your character's arc and emotions throughout the project. Better performances come from understanding the big picture!"
      }
    ]
  },
  {
    id: "actor-recording",
    title: "Recording Your Takes",
    icon: Mic,
    content: [
      {
        title: "Audio vs Video Takes",
        description: "You have two options for recording:",
        list: [
          { name: "Audio Takes", icon: Mic, desc: "Just your voice. Perfect for most dialogue." },
          { name: "Video Takes", icon: Video, desc: "Record yourself acting out the scene. This helps animators match your movements and expressions." }
        ]
      },
      {
        title: "Recording Audio",
        steps: [
          "Click the microphone button to start recording",
          "Speak your lines clearly",
          "Click stop when you're done",
          "Listen back to check it sounds good",
          "Not happy? Record another take!"
        ]
      },
      {
        title: "Recording Video",
        steps: [
          "Click the video camera button",
          "Position yourself in frame",
          "Act out the scene with your lines",
          "Click stop when done",
          "Review and record more takes if needed"
        ]
      },
      {
        title: "Uploading Pre-Recorded Files",
        description: "Already recorded somewhere else? No problem! Click the upload button and select your audio or video file. We support MP3, WAV, and most video formats."
      }
    ]
  },
  {
    id: "actor-managing",
    title: "Managing Your Takes",
    icon: ListChecks,
    content: [
      {
        title: "All Your Takes in One Place",
        description: "Every take you record is saved automatically. You can see all your takes for each shot listed below the recording area."
      },
      {
        title: "What You Can Do",
        list: [
          { name: "Play any take", desc: "Click to listen or watch it back" },
          { name: "Delete takes", desc: "Click the trash icon to remove ones you don't want" },
          { name: "Mark favorites", desc: "Click the star to flag takes you think are your best work" },
          { name: "Add notes", desc: "Leave a comment about a take (e.g., 'More energy' or 'Best one!')" }
        ]
      },
      {
        title: "How Many Takes Should You Do?",
        description: "That's up to you and your director! Some actors do 2-3 takes per line, others do more. A good rule: if you're not sure, do one more. Directors love having options."
      }
    ]
  },
  {
    id: "actor-tips",
    title: "Tips for Great Recordings",
    icon: Star,
    content: [
      {
        title: "Recording Environment",
        list: [
          { name: "Find a quiet space", desc: "Turn off fans, close windows, silence your phone" },
          { name: "Avoid echo", desc: "Closets or rooms with soft furniture work great" },
          { name: "Stay consistent", desc: "Record all your takes in the same spot if possible" },
          { name: "Test first", desc: "Do a quick test recording to check your levels and sound quality" }
        ]
      },
      {
        title: "Performance Tips",
        list: [
          { name: "Speak clearly", desc: "Articulate your words so your performance comes through crystal clear" },
          { name: "Match the energy", desc: "Read the context and match the emotional tone of the scene" },
          { name: "Don't rush", desc: "Natural pacing sounds better than racing through lines" },
          { name: "Have fun!", desc: "Your enthusiasm comes through in the recording - that's irreplaceable" }
        ]
      },
      {
        title: "Video Recording Tips",
        description: "Your video references help the animation match your unique performance style:",
        list: [
          { name: "Good lighting", desc: "Face a window or light source so your face is visible" },
          { name: "Stable camera", desc: "Prop your phone or use a tripod for steady video" },
          { name: "Show your face", desc: "Your facial expressions guide how the character emotes" },
          { name: "Use gestures", desc: "Your natural movements inspire how the character moves" }
        ]
      },
      {
        title: "Your Performance Matters",
        description: "Here's the thing: great animation starts with great performance. The way you deliver a line - the pause before a joke, the crack in your voice during an emotional moment - that's what makes a character feel real. We can animate mouths and movements, but only you can bring the soul."
      }
    ]
  },
  {
    id: "actor-troubleshooting",
    title: "Need Help?",
    icon: HelpCircle,
    content: [
      {
        title: "Common Questions",
        list: [
          { name: "Can't log in?", desc: "Check your email for the setup link. If it's expired, ask the director to resend it." },
          { name: "Don't see your scenes?", desc: "The director needs to assign you to characters. Reach out to them." },
          { name: "Recording not working?", desc: "Make sure you've allowed microphone/camera access in your browser." },
          { name: "Uploaded wrong file?", desc: "Just delete the take and upload the correct one." }
        ]
      },
      {
        title: "Still Stuck?",
        description: "Contact your director directly - they have access to all project settings and can help troubleshoot. You can also reach out to us at support@anim8pro.com."
      }
    ]
  }
];

// ============== GENERAL SECTIONS (for everyone) ==============
const generalSections = [
  {
    id: "keyboard-shortcuts",
    title: "Keyboard Shortcuts",
    icon: Settings,
    content: [
      {
        title: "Quick Navigation",
        list: [
          { name: "Space", desc: "Play/pause video preview" },
          { name: "Arrow keys", desc: "Navigate between takes" },
          { name: "Escape", desc: "Close modal or cancel action" },
          { name: "Ctrl/Cmd + S", desc: "Save your work" }
        ]
      }
    ]
  },
  {
    id: "contact",
    title: "Contact Support",
    icon: MessageSquare,
    content: [
      {
        title: "We're Here to Help",
        description: "Running into issues? Have questions? We'd love to hear from you.",
        list: [
          { name: "Email", desc: "support@anim8pro.com" },
          { name: "Response Time", desc: "Usually within 24 hours" }
        ]
      }
    ]
  }
];

export default function HelpDesk() {
  const navigate = useNavigate();
  const { isActor, isDirector, isSuperAdmin } = useAuth();
  const contentAreaRef = useRef(null);
  const sectionRefs = useRef({});
  
  // Determine default tab based on user role
  const getDefaultTab = () => {
    if (isActor && !isDirector && !isSuperAdmin) return "actor";
    return "director";
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [expandedSections, setExpandedSections] = useState(["director-welcome", "actor-welcome"]);
  const [activeSection, setActiveSection] = useState(activeTab === "actor" ? "actor-welcome" : "director-welcome");
  const [searchQuery, setSearchQuery] = useState("");

  // Get sections based on active tab
  const sections = useMemo(() => {
    if (activeTab === "director") return [...directorSections, ...generalSections];
    if (activeTab === "actor") return [...actorSections, ...generalSections];
    return [...directorSections, ...actorSections, ...generalSections];
  }, [activeTab]);

  // Search functionality
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    
    const query = searchQuery.toLowerCase();
    return sections.filter(section => {
      // Check section title
      if (section.title.toLowerCase().includes(query)) return true;
      
      // Check content
      return section.content.some(item => {
        if (item.title?.toLowerCase().includes(query)) return true;
        if (item.description?.toLowerCase().includes(query)) return true;
        if (item.steps?.some(step => step.toLowerCase().includes(query))) return true;
        if (item.list?.some(li => 
          li.name?.toLowerCase().includes(query) || 
          li.desc?.toLowerCase().includes(query)
        )) return true;
        if (item.code?.toLowerCase().includes(query)) return true;
        return false;
      });
    });
  }, [searchQuery, sections]);

  // Handle section click from left menu - expand AND scroll to section
  const handleSectionClick = (sectionId) => {
    // Always expand the section
    setExpandedSections(prev => 
      prev.includes(sectionId) ? prev : [...prev, sectionId]
    );
    setActiveSection(sectionId);
    
    // Scroll to the section after a brief delay for DOM update
    setTimeout(() => {
      const sectionEl = sectionRefs.current[sectionId];
      if (sectionEl && contentAreaRef.current) {
        sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    setActiveSection(sectionId);
  };

  const renderContent = (item, index) => {
    return (
      <div key={index} className="mb-6 last:mb-0">
        <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
        
        {item.description && (
          <p className="text-zinc-400 mb-3 leading-relaxed">{item.description}</p>
        )}
        
        {item.steps && (
          <ol className="list-decimal list-inside space-y-2 text-zinc-300 ml-2">
            {item.steps.map((step, i) => (
              <li key={i} className="pl-2 leading-relaxed">{step}</li>
            ))}
          </ol>
        )}
        
        {item.list && (
          <div className="space-y-2 mt-3">
            {item.list.map((listItem, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                {listItem.icon && <listItem.icon className="w-5 h-5 text-indigo-400 mt-0.5 shrink-0" />}
                {!listItem.icon && <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />}
                <div>
                  <span className="text-white font-medium">{listItem.name}</span>
                  {listItem.desc && (
                    <span className="text-zinc-400"> — {listItem.desc}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {item.code && (
          <pre className="bg-zinc-800 p-4 rounded-lg text-sm text-zinc-300 overflow-x-auto mt-3 font-mono whitespace-pre-wrap">
            {item.code}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-900/80 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Help Desk</h1>
                  <p className="text-sm text-zinc-500">Everything you need to know about anim8pro</p>
                </div>
              </div>
            </div>
            <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
              v2.0
            </Badge>
          </div>
          
          {/* Search and Tabs */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="help-search"
              />
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
              <TabsList className="bg-zinc-800 border border-zinc-700">
                <TabsTrigger 
                  value="director" 
                  className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white"
                  data-testid="tab-director"
                >
                  <Clapperboard className="w-4 h-4 mr-2" />
                  Director Guide
                </TabsTrigger>
                <TabsTrigger 
                  value="actor" 
                  className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"
                  data-testid="tab-actor"
                >
                  <Headphones className="w-4 h-4 mr-2" />
                  Actor Guide
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex h-[calc(100vh-145px)]">
        {/* Sidebar Navigation - Fixed */}
        <aside className="w-64 shrink-0 border-r border-zinc-800 hidden md:block overflow-y-auto">
          <nav className="p-4 space-y-1 sticky top-0">
            {filteredSections.map((section) => (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? activeTab === "actor" 
                      ? "bg-amber-500/20 text-amber-400"
                      : "bg-indigo-500/20 text-indigo-400"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
                data-testid={`nav-${section.id}`}
              >
                <section.icon className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium truncate">{section.title}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content - Scrollable */}
        <main ref={contentAreaRef} className="flex-1 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-3xl">
            {/* Search Results Count */}
            {searchQuery && (
              <div className="mb-4 text-sm text-zinc-400">
                Found {filteredSections.length} article{filteredSections.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
              </div>
            )}
            
            {filteredSections.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No results found</h3>
                  <p className="text-zinc-400">Try searching for something else or browse the categories above.</p>
                </CardContent>
              </Card>
            ) : (
              filteredSections.map((section) => (
                <Card 
                  key={section.id} 
                  ref={el => sectionRefs.current[section.id] = el}
                  className="bg-zinc-900/50 border-zinc-800 mb-4 scroll-mt-4"
                  data-testid={`section-${section.id}`}
                >
                  <CardHeader 
                    className="cursor-pointer"
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          section.id.startsWith('actor') 
                            ? 'bg-amber-500/20' 
                            : 'bg-indigo-500/20'
                        }`}>
                          <section.icon className={`w-5 h-5 ${
                            section.id.startsWith('actor') 
                              ? 'text-amber-400' 
                              : 'text-indigo-400'
                          }`} />
                        </div>
                        <CardTitle className="text-white">{section.title}</CardTitle>
                      </div>
                      {expandedSections.includes(section.id) ? (
                        <ChevronDown className="w-5 h-5 text-zinc-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                  </CardHeader>
                  {expandedSections.includes(section.id) && (
                    <CardContent className="pt-0">
                      <div className="border-t border-zinc-800 pt-4">
                        {section.content.map((item, index) => renderContent(item, index))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </div>

          {/* Quick Links Footer */}
          <div className="max-w-3xl mt-8 p-6 bg-zinc-900/30 rounded-xl border border-zinc-800">
            <h3 className="text-lg font-semibold text-white mb-4">Need More Help?</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="justify-start gap-3 h-auto py-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => navigate("/")}
              >
                <Film className="w-5 h-5 text-indigo-400" />
                <div className="text-left">
                  <div className="font-medium">Go to Dashboard</div>
                  <div className="text-xs text-zinc-500">Start creating</div>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="justify-start gap-3 h-auto py-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => window.location.href = "mailto:support@anim8pro.com"}
              >
                <HelpCircle className="w-5 h-5 text-amber-400" />
                <div className="text-left">
                  <div className="font-medium">Contact Support</div>
                  <div className="text-xs text-zinc-500">support@anim8pro.com</div>
                </div>
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
